import API from '@/api/apiCreate'
import { getParams } from '@/api/getParams'
import { isError } from '@/helpers/checkQuery'
import { ResponseWrapper } from '@/api/types/base.types'
import type {
  GetTaskTrackerSummaryViewParams,
  TaskTrackerSummaryItemsPage,
} from '../store/taskTracker.types'
import type {
  TaskTrackerBoardSummary,
  TaskTrackerBoardSummaryMutationResult,
  TaskTrackerBoardSummaryPreview,
  TaskTrackerBoardSummarySavePayload,
} from '../lib/taskTrackerBoardSummary.types'
import { getApiErrorMessage, TASK_TRACKER_API_REQUEST_CONFIG } from './taskTracker.api.utils'
import {
  mapTaskTrackerSummaryFromApi,
  mapTaskTrackerSummaryMutationFromApi,
  mapTaskTrackerSummaryPreviewFromApi,
  mapTaskTrackerSummarySavePayloadToApi,
} from './taskTracker.summaries.api.mappers'
import type {
  ApiTaskTrackerSummariesIndexResponse,
  ApiTaskTrackerSummaryDeleteResponse,
  ApiTaskTrackerSummaryMutationResponse,
  ApiTaskTrackerSummaryViewResponse,
} from './taskTracker.api.types'

export const taskTrackerSummariesApi = {
  async listSummaries(): Promise<TaskTrackerBoardSummary[]> {
    const res = await API.get<ResponseWrapper<ApiTaskTrackerSummariesIndexResponse>>(
      'task-tracker-summaries/index',
      TASK_TRACKER_API_REQUEST_CONFIG,
    )

    if (isError(res)) {
      throw new Error(getApiErrorMessage(res, 'Не удалось загрузить список сводок'))
    }

    return (res.data?.data?.summaries ?? []).map(mapTaskTrackerSummaryFromApi)
  },

  async getSummaryView(
    summaryId: number,
    params?: Omit<GetTaskTrackerSummaryViewParams, 'summaryId'>,
  ): Promise<TaskTrackerBoardSummaryPreview> {
    const res = await API.get<ResponseWrapper<ApiTaskTrackerSummaryViewResponse>>(
      `task-tracker-summaries/view${getParams({
        summary_id: summaryId,
        cursor: params?.cursor ?? undefined,
        limit: params?.limit ?? undefined,
      })}`,
      TASK_TRACKER_API_REQUEST_CONFIG,
    )

    if (isError(res)) {
      throw new Error(getApiErrorMessage(res, 'Не удалось открыть сводку'))
    }

    if (!res.data?.data?.summary) {
      throw new Error('Не удалось открыть сводку')
    }

    return mapTaskTrackerSummaryPreviewFromApi(res.data.data)
  },

  async getSummaryItems(
    params: GetTaskTrackerSummaryViewParams,
  ): Promise<TaskTrackerSummaryItemsPage> {
    const preview = await this.getSummaryView(params.summaryId, params)

    return {
      summaryId: params.summaryId,
      items: preview.items,
      nextCursor: preview.nextCursor ?? null,
      hasMore: preview.hasMore,
      totalCount: preview.totalCount,
    }
  },

  async previewSummary(
    payload: TaskTrackerBoardSummarySavePayload,
    boardId?: number | null,
  ): Promise<TaskTrackerBoardSummaryPreview> {
    const res = await API.post<ResponseWrapper<ApiTaskTrackerSummaryViewResponse>>(
      `task-tracker-summaries/preview${getParams({
        board_id: boardId ?? undefined,
      })}`,
      mapTaskTrackerSummarySavePayloadToApi(payload),
      TASK_TRACKER_API_REQUEST_CONFIG,
    )

    if (isError(res)) {
      throw new Error(getApiErrorMessage(res, 'Не удалось построить предпросмотр сводки'))
    }

    if (!res.data?.data?.summary) {
      throw new Error('Не удалось построить предпросмотр сводки')
    }

    return mapTaskTrackerSummaryPreviewFromApi(res.data.data)
  },

  async createSummary(
    payload: TaskTrackerBoardSummarySavePayload,
    boardId?: number | null,
  ): Promise<TaskTrackerBoardSummaryMutationResult> {
    const res = await API.post<ResponseWrapper<ApiTaskTrackerSummaryMutationResponse>>(
      `task-tracker-summaries/create${getParams({
        board_id: boardId ?? undefined,
      })}`,
      mapTaskTrackerSummarySavePayloadToApi(payload),
      TASK_TRACKER_API_REQUEST_CONFIG,
    )

    if (isError(res)) {
      throw new Error(getApiErrorMessage(res, 'Не удалось создать сводку'))
    }

    if (!res.data?.data?.summary) {
      throw new Error('Не удалось создать сводку')
    }

    return mapTaskTrackerSummaryMutationFromApi(res.data.data)
  },

  async updateSummary(
    summaryId: number,
    payload: TaskTrackerBoardSummarySavePayload,
  ): Promise<TaskTrackerBoardSummaryMutationResult> {
    const res = await API.post<ResponseWrapper<ApiTaskTrackerSummaryMutationResponse>>(
      `task-tracker-summaries/update${getParams({ summary_id: summaryId })}`,
      mapTaskTrackerSummarySavePayloadToApi(payload),
      TASK_TRACKER_API_REQUEST_CONFIG,
    )

    if (isError(res)) {
      throw new Error(getApiErrorMessage(res, 'Не удалось сохранить сводку'))
    }

    if (!res.data?.data?.summary) {
      throw new Error('Не удалось сохранить сводку')
    }

    return mapTaskTrackerSummaryMutationFromApi(res.data.data)
  },

  async deleteSummary(summaryId: number): Promise<{ deleted: boolean; summaryId: number }> {
    const res = await API.post<ResponseWrapper<ApiTaskTrackerSummaryDeleteResponse>>(
      `task-tracker-summaries/delete${getParams({ summary_id: summaryId })}`,
      {},
      TASK_TRACKER_API_REQUEST_CONFIG,
    )

    if (isError(res)) {
      throw new Error(getApiErrorMessage(res, 'Не удалось удалить сводку'))
    }

    return {
      deleted: Boolean(res.data?.data?.deleted),
      summaryId: Number(res.data?.data?.summary_id ?? summaryId),
    }
  },
}
